import { FilterValue } from "../Filters/FilterValue";
import { ModuleTasks } from "../ModuleType";
import { OrderMockup } from "../Order/OrderMockup";
import { OrderProduct } from "../Order/OrderProduct";
import {
  InventoryOpsSkuVariantLine,
  VendorInvoice,
} from "./InventoryOpsRequests";
import { PurchaseOrder } from "./PurchaseOrder";

export interface ProjectInventoryOpsProduct {
  purchase_orders: PurchaseOrder[];
  print_inward_timelines: PrintInward[];
  order_id?: number;
  id: number;
  name: string;
  quantity: number;
  customisation_required?: boolean;
  used_quantity: number;
  sku?: string;
  image_url?: string;
  image_urls?: string[];
  mockups?: OrderMockup[];
  category_id?: number;
  category_name?: string;
  active?: boolean;
  cost_price: number;
  unit_price: number;
  hsn_code?: string;
  gst_percentage?: number;

  raw_inward_qty?: number;
  raw_inward_qc_rejection_qty?: number;
  print_outward_qty?: number;
  print_inward_qty?: number;
  print_inward_qc_rejection_qty?: number;

  completed_tasks?: {
    raw_inward: boolean;
    raw_inward_qc: boolean;
    print_outward: boolean;
    print_inward: boolean;
    print_inward_qc: boolean;
  };

  completed_task_count?: number;
}

export interface InventoryOpsDetails {
  brand_name: string;
  raw_inward: RawInward[];
  raw_inward_qc: RawInwardQc[];
  print_outward: PrintOutward[];
  print_inward: PrintInward[];
  print_inward_qc: PrintInwardQc[];
}

export interface RawInward {
  id: number;
  po_id: number;
  product_id: number;
  quantity_received: number;
  total_received_quantity: number;
}

export interface RawInwardQc {
  product_id: number;
  rejected_quantity: number;
  rejection_reason: string;
  total_rejected_quantity: number;
}

export interface PrintOutward {
  modified_date: Date;
  printer_id: number;
  product_id: number;
  quantity_sent: number;
  raw_inward_id: number;
  storage_id: number;
  timeline: Date;
  total_quantity_sent: number;
}

export interface PrintInward {
  active?: number;
  admin_id?: number;
  created_date?: Date;
  id?: number;
  modified_date?: Date;
  print_outward_id?: number;
  product_id?: number;
  quantity_received?: number;
  total_received_quantity?: number;
  raw_inward_id?: number;
  po_id?: number;

  timeline?: string;
  expected_delivery_date?: string;
}

export interface PrintInwardQc {
  active: number;
  admin_id: number;
  created_date: Date;
  id: number;
  modified_date: Date;
  print_inward_id: number;
  product_id: number;
  rejected_quantity: number;
  rejection_reason: string;
  total_rejected_quantity: number;
}

export interface InventoryListingDetails {
  module_tasks: ModuleTasks[];
  brand_name: string;
  label: string;
  products: ProjectInventoryOpsProduct[];
  raw_inward_qc_percentage: number;
  print_inward_qc_percentage: number;
  total_progress: number;
  archived?: number;
  inventory_id?: number | null;
  /** 1 = Master Inventory, 2 = Order Inventory */
  inventory_type?: number | null;
}

export interface InventoryOpsSizeMix {
  id: number;
  order_id?: number;
  product_id?: number;
  title?: string | null;
  sku: string;
  quantity: number;
  active?: number;
}

export interface InventoryOpsSkuVariantBase {
  variant_id: number;
  sku: string;
  title?: string | null;
  created_date?: string | Date | null;
  modified_date?: string | Date | null;
}

/** Nested variant row for raw-inward / print-inward received quantities. */
export interface InventoryOpsReceivedSkuVariant extends InventoryOpsSkuVariantBase {
  quantity_received?: number;
  total_received_quantity?: number;
}

/** Nested variant row for raw / print QC (rejected + passed). */
export interface InventoryOpsRejectedSkuVariant extends InventoryOpsSkuVariantBase {
  rejected_quantity?: number;
  total_rejected_quantity?: number;
  /** Null on rows saved before passed qty was tracked. */
  passed_quantity?: number | null;
  total_passed_quantity?: number | null;
}

/**
 * QC progress for raw / print inward.
 * UNCONFIRMED = QC saved before passed qty was tracked; pending is unknown.
 */
/**
 * How a raw / print inward's qty is recorded: QTY = via the /qty endpoint,
 * QC = counted during QC (tested qty is added to received).
 */
export type InventoryOpsRawInwardQtySource = "QTY" | "QC";

export type InventoryOpsQcStatus =
  "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "UNCONFIRMED";

/** Nested variant row for print outward sent quantities. */
export interface InventoryOpsSentSkuVariant extends InventoryOpsSkuVariantBase {
  quantity_sent?: number;
  total_quantity_sent?: number;
}

/** PO-level stage timestamps. Null when that stage has no row. */
export interface InventoryOpsStageTimestamps {
  raw_inward_first_inward?: string | Date | null;
  raw_inward_last_inward?: string | Date | null;
  raw_inward_qc_start_time?: string | Date | null;
  raw_inward_qc_end_time?: string | Date | null;
  /** QC time spent, in seconds. */
  raw_inward_qc_cumulative?: number | null;
  print_outward_first_out?: string | Date | null;
  print_outward_last_out?: string | Date | null;
  print_inward_first_inward?: string | Date | null;
  print_inward_last_inward?: string | Date | null;
  print_inward_qc_start_time?: string | Date | null;
  print_inward_qc_end_time?: string | Date | null;
  /** QC time spent, in seconds. */
  print_inward_qc_cumulative?: number | null;
}

export const INVENTORY_OPS_STAGE_TIMESTAMP_KEYS: Array<
  keyof InventoryOpsStageTimestamps
> = [
  "raw_inward_first_inward",
  "raw_inward_last_inward",
  "raw_inward_qc_start_time",
  "raw_inward_qc_end_time",
  "raw_inward_qc_cumulative",
  "print_outward_first_out",
  "print_outward_last_out",
  "print_inward_first_inward",
  "print_inward_last_inward",
  "print_inward_qc_start_time",
  "print_inward_qc_end_time",
  "print_inward_qc_cumulative",
];

export const pickInventoryOpsStageTimestamps = (
  ...sources: Array<InventoryOpsStageTimestamps | null | undefined>
): InventoryOpsStageTimestamps => {
  const result: InventoryOpsStageTimestamps = {};
  INVENTORY_OPS_STAGE_TIMESTAMP_KEYS.forEach((key) => {
    for (const source of sources) {
      const value = source?.[key];
      if (value !== undefined) {
        (result[key] as InventoryOpsStageTimestamps[typeof key]) = value;
        break;
      }
    }
  });
  return result;
};

export interface InventoryOpsPoDetail extends InventoryOpsStageTimestamps {
  po_id: number;
  warehouse_id?: string | null;
  warehouse_identifier?: string | null;
  raw_inward_id?: number | null;
  raw_inward_quantity?: number | null;
  raw_inward_rejection_quantity?: number | null;
  raw_inward_rejection_reason?: string | null;
  /** Latest raw inward update time (product-level freshness). */
  raw_inward_modified_date?: string | Date | null;
  /** Latest raw QC update time (product-level freshness). */
  raw_inward_qc_modified_date?: string | Date | null;
  print_inward_due_date?: string;
  print_outward_id?: number;
  print_outward_printer_id?: number;
  storage_id?: number;
  print_outward_printer_name?: string;
  print_outward_quantity?: number;
  print_outward_timeline?: Date;
  print_outward_modified_date?: string | Date | null;
  print_inward_id?: number | null;
  print_inward_quantity?: number | null;
  print_inward_rejection_quantity?: number | null;
  print_inward_rejection_reason?: string | null;
  print_inward_modified_date?: string | Date | null;
  print_inward_qc_modified_date?: string | Date | null;

  raw_inward_qty_source?: InventoryOpsRawInwardQtySource | null;
  print_inward_qty_source?: InventoryOpsRawInwardQtySource | null;
  /** Stored passed totals (null = never recorded). */
  raw_inward_passed_quantity?: number | null;
  print_inward_passed_quantity?: number | null;
  raw_inward_qc_status?: InventoryOpsQcStatus | null;
  /** Tested so far = passed + rejected; null when UNCONFIRMED / untracked. */
  raw_inward_qc_checked_quantity?: number | null;
  raw_inward_qc_passed_quantity?: number | null;
  /** received − passed − rejected; null when untracked. */
  raw_inward_qc_pending_quantity?: number | null;
  print_inward_qc_status?: InventoryOpsQcStatus | null;
  print_inward_qc_checked_quantity?: number | null;
  print_inward_qc_passed_quantity?: number | null;
  print_inward_qc_pending_quantity?: number | null;

  product_po_quantity?: number;
  vendor_name?: string;

  /** True when that record has a batch Undo last can remove (false for batches saved before undo existed). */
  raw_inward_undo_available?: boolean;
  print_outward_undo_available?: boolean;
  print_inward_undo_available?: boolean;
  raw_inward_qc_images?: QcImages[] | null;
  print_inward_qc_images?: QcImages[] | null;

  raw_inward_sku_variants?: InventoryOpsReceivedSkuVariant[] | null;
  raw_inward_qc_sku_variants?: InventoryOpsRejectedSkuVariant[] | null;
  print_outward_sku_variants?: InventoryOpsSentSkuVariant[] | null;
  print_inward_sku_variants?: InventoryOpsReceivedSkuVariant[] | null;
  print_inward_qc_sku_variants?: InventoryOpsRejectedSkuVariant[] | null;

  temp_raw_inward_quantity?: number | null;
  temp_raw_inward_sku_variants?: InventoryOpsSkuVariantLine[] | null;
  temp_raw_inward_rejection_quantity?: number | null;
  temp_raw_inward_tested_quantity?: number | null;
  temp_raw_inward_qc_sku_variants?: InventoryOpsSkuVariantLine[] | null;
  temp_raw_inward_rejection_reason?: string | null;
  temp_print_inward_due_date?: string | null;
  temp_print_outward_id?: number | null;
  temp_print_outward_printer_id?: number | null;
  temp_storage_id?: number | null;
  temp_print_outward_printer_name?: string | null;
  temp_print_outward_quantity?: number | null;
  temp_print_outward_sku_variants?: InventoryOpsSkuVariantLine[] | null;
  temp_print_outward_timeline?: Date | null;
  temp_print_inward_id?: number | null;
  temp_print_inward_quantity?: number | null;
  temp_print_inward_sku_variants?: InventoryOpsSkuVariantLine[] | null;
  temp_print_inward_rejection_quantity?: number | null;
  temp_print_inward_tested_quantity?: number | null;
  temp_print_inward_qc_sku_variants?: InventoryOpsSkuVariantLine[] | null;
  temp_print_inward_rejection_reason?: string | null;
  temp_raw_inward_qc_images?: File[] | null;
  temp_print_inward_qc_images?: File[] | null;
}

export interface VendorInvoiceDetails {
  invoice_received?: boolean;
  invoice_received_updated_at?: Date;
  invoice_received_updated_by?: number;
  invoice_received_updated_by_name?: string;

  invoices?: VendorInvoice[];
}

export interface ProjectInventoryOpsProductDetails {
  product: OrderProduct;
  purchase_orders: PurchaseOrder[];
  mockups: OrderMockup[];
}

export interface ProjectInventoryOpsProductDetailsForPurchaseOrder extends InventoryOpsStageTimestamps {
  size_mixes: InventoryOpsSizeMix[];
  progress: number;
  inventory_ops_details: InventoryOpsPoDetail[];
}

export interface ProjectInventoryOpsSummary {
  po_id: number;
  raw_inward_id: number;
  history: {
    [date: string]: SummaryHistory[];
  };
}

export interface QcImages {
  id: number;
  admin_id: number;
  image_url: string;
  print_inward_id: number;
}
export interface SummaryHistory {
  po_id: number | null;
  type: string;
  id: number;
  printer_id?: number;
  print_inward_id?: number;
  raw_inward_id?: number;
  print_outward_id?: number;
  quantity_received?: number;
  total_received_quantity?: number;
  mark_received?: number;
  quantity_sent?: number;
  total_quantity_sent?: number;
  storage_id?: number;
  rejection_reason?: string | null;
  rejected_quantity?: number;
  total_rejected_quantity?: number;
  order_id?: number;
  product_id?: number;
  admin_id: number;
  active: number;
  created_date: string;
  modified_date: string;
  start_time?: string;
  end_time?: string | null;
  cumulative_time?: number;
  timeline?: string;
  admin_name: string;
  printer_name?: string;
  image_url?: string;
}

export enum InvoicePaymentStatus {
  PAID = 1,
  PARTIAL = 2,
  PENDING = 3,
}

export const InvoicePaymentStatusFilters: FilterValue[] = [
  { id: "paid", value: "Paid" },
  { id: "partial_paid", value: "Partial" },
  { id: "pending", value: "Pending" },
];
